import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import NotFound from '../NotFound.jsx';
import '../../styles/InvitationLoader.css';
import logicaLogo from '../../assets/images/Logica (Black and White).webp';

const designPageLoaders = import.meta.glob('./designs/*.jsx');

function normalizeInvitationId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

const loadersByInvitationId = Object.fromEntries(
  Object.entries(designPageLoaders).map(([filePath, loader]) => {
    const match = filePath.match(/\/([^/]+)\.jsx$/);
    const derivedInvitationId = normalizeInvitationId(match?.[1] || '');
    return [derivedInvitationId, loader];
  })
);

export default function CustomDesignInvitation() {
  const { invitationId } = useParams();
  const normalizedInvitationId = useMemo(() => normalizeInvitationId(invitationId), [invitationId]);
  const [DesignComponent, setDesignComponent] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let isActive = true;
    const loader = loadersByInvitationId[normalizedInvitationId];

    setDesignComponent(null);

    if (!loader) {
      setStatus('not-found');
      return () => {
        isActive = false;
      };
    }

    setStatus('loading');

    loader()
      .then((module) => {
        if (!isActive || typeof module.default !== 'function') return;
        setDesignComponent(() => module.default);
        setStatus('ready');
      })
      .catch(() => {
        if (isActive) setStatus('not-found');
      });

    return () => {
      isActive = false;
    };
  }, [normalizedInvitationId]);

  if (status === 'not-found') {
    return <NotFound />;
  }

  if (status !== 'ready' || !DesignComponent) {
    return (
      <main className="inv-loader-page" aria-live="polite" aria-label="Loading invitation">
        {/* Floating fireflies */}
        <div className="inv-loader-particles" aria-hidden="true">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="inv-loader-particle" />
          ))}
        </div>

        {/* Center content */}
        <div className="inv-loader-center">
          {/* Ornament */}
          <div className="inv-loader-ornament" aria-hidden="true">
            <div className="inv-loader-ornament-line" />
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z"
                stroke="currentColor" strokeWidth="1" fill="currentColor" fillOpacity="0.25" />
            </svg>
            <div className="inv-loader-ornament-line" />
          </div>

          {/* Script headline */}
          <p className="inv-loader-script">Your Invitation</p>

          {/* Subtitle */}
          <p className="inv-loader-subtitle">Awaits &nbsp;✦&nbsp; Preparing your experience</p>

          {/* Sweeping progress bar */}
          <div className="inv-loader-bar-wrap" role="progressbar" aria-label="Loading">
            <div className="inv-loader-bar" />
          </div>

          {/* Dot pulse */}
          <div className="inv-loader-dots" aria-hidden="true">
            <div className="inv-loader-dot" />
            <div className="inv-loader-dot" />
            <div className="inv-loader-dot" />
          </div>
        </div>

        {/* Brand */}
        <a
          href="https://logicatechnology.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inv-loader-brand"
          aria-label="Logica Technology"
        >
          <img src={logicaLogo} alt="" aria-hidden="true" />
          <span>Logica Technology</span>
        </a>
      </main>
    );
  }

  return <DesignComponent />;
}